import type { FeatureKey } from '@gbv/types';
import {
  BarChart3,
  Brush,
  Building2,
  Contact,
  CalendarDays,
  Database,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Globe,
  Image,
  IndianRupee,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  Mail,
  MessageSquare,
  NotebookPen,
  Palette,
  Phone,
  Plug,
  Receipt,
  Search,
  Send,
  Share2,
  ShieldCheck,
  StickyNote,
  Target,
  Trash2,
  UserSquare2,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Hidden unless the active membership grants this. */
  feature?: FeatureKey;
  /**
   * Hidden unless the membership grants at least one of these.
   *
   * Needed wherever a capability is split into do-it and view-it keys: an
   * admin who grants only "Invoices — create and send" would otherwise get a
   * working route with no way to reach it.
   */
  anyFeature?: FeatureKey[];
  /** Route exists in the product map but is not built yet. */
  planned?: boolean;
}

export interface NavGroup {
  /** Undefined for the ungrouped items that sit at the top. */
  department?: string;
  icon: LucideIcon;
  items: NavItem[];
}

/**
 * The nav has two halves, and the divide is the point.
 *
 *   client    everything scoped to the client named in the chip above the
 *             working area. Switching client changes what these screens show.
 *   internal  GoBeeVir's own work. No client, no org id, and nothing here ever
 *             appears in a client's working area.
 *
 * Two people share this sidebar all day: one doing work *for* a client and one
 * doing work *at* the agency. Mixing "Tasks" for a client with "Tasks" for the
 * team in a single list is how an internal note ends up on a client's board.
 */
export type NavSection = 'client' | 'internal';

export interface NavSectionGroup {
  section: NavSection;
  label: string;
  hint: string;
  groups: NavGroup[];
}

/**
 * The employee workspace navigation, grouped by department.
 *
 * Items the person's role does not grant are hidden entirely rather than shown
 * disabled — a sidebar full of locked doors is worse than a short one. Items
 * marked `planned` are on the product map but not built; they render with a
 * quiet marker so the shape of the platform stays visible without pretending
 * the screen works.
 */
export const NAVIGATION: NavGroup[] = [
  {
    icon: LayoutDashboard,
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard },
    ],
  },
  {
    department: 'Sales & Revenue',
    icon: Target,
    items: [
      { label: 'Leads', to: '/leads', icon: Users, feature: 'crm' },
      // Ordered the way the work flows: a lead becomes a contact, a contact
      // belongs to a company, a company has deals. Contacts used to sit beside
      // Dashboard, away from the three records it is worked alongside.
      { label: 'Contacts', to: '/people', icon: Contact, feature: 'crm' },
      { label: 'Companies', to: '/companies', icon: Building2, feature: 'crm' },
      { label: 'Opportunities', to: '/opportunities', icon: Target, feature: 'crm' },
      // Quotes, proposals and proformas. The tax invoice is the same editor
      // but it belongs to the books, so it lives under Finance & Legal.
      { label: 'Quotes & proposals', to: '/documents', icon: FileText, feature: 'invoicing' },
      { label: 'Analytics', to: '/analytics', icon: BarChart3, feature: 'analytics' },
    ],
  },
  {
    department: 'Operations & Support',
    icon: ListChecks,
    items: [
      { label: 'Daily report', to: '/report', icon: Send, feature: 'dailyReport' },
      { label: 'Tasks', to: '/tasks', icon: ListChecks, feature: 'tasks' },
      // One entry, not two. "Appointments" and "Calendar" both opened the same
      // screen — the /appointments route is kept so existing links and
      // bookmarks still resolve, but it has no reason to sit in the sidebar
      // twice under two names.
      { label: 'Calendar', to: '/calendar', icon: CalendarDays, feature: 'appointments' },
      { label: 'Mails', to: '/mails', icon: Mail },
      { label: 'Drive', to: '/drive', icon: FolderOpen, feature: 'assets' },
      { label: 'Office', to: '/office', icon: FileText, feature: 'assets' },
      { label: 'Calls', to: '/calls', icon: Phone, feature: 'dialer' },
      /*
       * Due diligence on a business before dealing with it. Operations
       * rather than Sales: the question is asked about a supplier or a
       * counterparty as often as about a prospect, and it is asked before
       * money moves rather than while selling.
       *
       * `companyVerification` is in no role's defaults, so this is invisible
       * until an admin grants it.
       */
      {
        label: 'Company verification',
        to: '/verification',
        icon: ShieldCheck,
        feature: 'companyVerification',
      },
      {
        label: 'WhatsApp inbox',
        to: '/inbox',
        icon: MessageSquare,
        feature: 'whatsappInbox',
        planned: true,
      },
      /*
       * Last in the section on purpose. It is not somewhere anyone works —
       * it is where you go when something has gone missing and you need to
       * know who removed it and what it said.
       *
       * `deletionHistory` is in no role's defaults, so it is invisible until
       * an admin grants it: the list carries the content of deleted notes and
       * mails, and can show more than the screens the reader can otherwise
       * open.
       */
      { label: 'Deleted items', to: '/deleted', icon: Trash2, feature: 'deletionHistory' },
    ],
  },
  {
    department: 'Finance & Legal',
    icon: IndianRupee,
    items: [
      {
        label: 'Tax invoices',
        to: '/invoices',
        icon: Receipt,
        anyFeature: ['invoicing', 'invoicingView'],
      },
      { label: 'GST', to: '/gst', icon: FileSpreadsheet, feature: 'invoicing', planned: true },
      { label: 'Expenses', to: '/expenses', icon: Wallet, feature: 'expenses', planned: true },
      { label: 'Contracts', to: '/contracts', icon: FileText, feature: 'assets', planned: true },
    ],
  },
  {
    department: 'People & HR',
    icon: UserSquare2,
    items: [
      { label: 'Employees', to: '/employees', icon: UserSquare2, feature: 'hr', planned: true },
      { label: 'Attendance', to: '/attendance', icon: CalendarDays, feature: 'hr', planned: true },
    ],
  },
  {
    department: 'Technology & Data',
    icon: Database,
    items: [
      {
        label: 'Integrations',
        to: '/integrations',
        icon: Plug,
        feature: 'integrations',
        planned: true,
      },
      {
        label: 'Data imports',
        to: '/imports',
        icon: Database,
        feature: 'dataImport',
        planned: true,
      },
    ],
  },
  {
    department: 'Product & Strategy',
    icon: Lightbulb,
    items: [
      { label: 'Roadmap', to: '/roadmap', icon: Lightbulb, planned: true },
      { label: 'Client plans', to: '/plans', icon: FileSpreadsheet, planned: true },
    ],
  },
  {
    department: 'Creative & Customer Experience',
    icon: Brush,
    items: [
      { label: 'Brand & design', to: '/design', icon: Palette, feature: 'assets' },
      { label: 'Client chats', to: '/chats', icon: MessageSquare },
    ],
  },
  {
    department: 'Digital Marketing',
    icon: Globe,
    items: [
      // Not finished. The page opens, but its traffic figures come from the
      // mock analytics adapter — Google Analytics and Search Console were
      // never connected. Badged so nobody reads those numbers as real.
      { label: 'Websites', to: '/websites', icon: Globe, feature: 'socialMedia', planned: true },
      { label: 'SEO', to: '/seo', icon: Search, feature: 'socialMedia', planned: true },
      {
        label: 'Content writing',
        to: '/contents',
        icon: FileText,
        feature: 'contentCalendar',
        planned: true,
      },
    ],
  },
  {
    department: 'Social Media Management',
    icon: Share2,
    items: [
      // Same reason as Websites: the page works, but followers, views and
      // engagement are generated by the mock social adapter rather than pulled
      // from Meta or LinkedIn.
      { label: 'Social media', to: '/social', icon: Share2, feature: 'socialMedia', planned: true },
    ],
  },
];

/**
 * The internal half: GoBeeVir's own work.
 *
 * None of these take an org id. Team chat lives here rather than under
 * Conversations because it is internal by definition — it was the one item in
 * the old list that belonged on this side of the line.
 */
export const INTERNAL_NAVIGATION: NavGroup[] = [
  {
    department: 'My work',
    icon: ListChecks,
    items: [
      { label: 'Internal tasks', to: '/internal/tasks', icon: ListChecks },
      { label: 'Internal calendar', to: '/internal/calendar', icon: CalendarDays },
      { label: 'Internal mail', to: '/internal/mails', icon: Mail },
    ],
  },
  {
    department: 'Team',
    icon: Users,
    items: [
      { label: 'Team chat', to: '/team', icon: MessageSquare },
      /*
       * Above the handover board on purpose. This is the one people reach for
       * mid-call; the board is the one they reach for at the end of a week.
       */
      { label: 'Quick notes', to: '/internal/quick-notes', icon: StickyNote },
      { label: 'Notes & handovers', to: '/internal/notes', icon: NotebookPen },
      { label: 'Directory', to: '/internal/directory', icon: Users },
    ],
  },
];

/** The two halves, in the order they render. */
export const NAV_SECTIONS: NavSectionGroup[] = [
  {
    section: 'client',
    label: 'Client work',
    hint: 'Scoped to the client selected above',
    groups: NAVIGATION,
  },
  {
    section: 'internal',
    label: 'Internal',
    hint: 'GoBeeVir’s own work — no client sees this',
    groups: INTERNAL_NAVIGATION,
  },
];
